import React, { useState, useEffect } from 'react';
import useStyles from './useStyles';
import RequestForQuotationDropzone from './RequestForQuotationDropzone';
import RequestForQuotationFileTable from './RequestForQuotationFileTable';
import RequestForQuotationFileCard from './RequestForQuotationFileCard';

import { clsx } from '@components/Provider';
import { CustomizedTypography } from '@components/Customized';

import { IRequestState, UploadedDrawingFileType } from '@modules/request';

import {
  CapaQuotationDetailedServiceType,
  CapaQuotationServiceType,
  CapaQuotationMaterial,
  CapaQuotationDetailedMaterialType
} from '@generated/graphql';

import { requestEnumToString } from '@utils/enumToString';
import OptionSelectDialog from './Dialogs/OptionSelectDialog';
import OptionSelectBatchDialog from './Dialogs/OptionSelectBatchDialog';
import { Controller, useFormContext } from 'react-hook-form';
import DeleteDrawingFileDialog from './Dialogs/DeleteDrawingFileDialog';
import { DialogTypes } from './types';
import PrintingTooltip from './PrintingTooltip';
import { formatCurrency } from '@utils/formatter';

interface Props {
  matches: boolean;
  requestState: IRequestState;
  uploadedFilesError: string[];
  handleUpdateRfqFile: (payload: UploadedDrawingFileType) => void;
  handleBatchUpdateRfqFile: (payload: UploadedDrawingFileType) => void;
  handleDeleteRfqFile: (payload: UploadedDrawingFileType) => void;
  onOpenDialog: (type: keyof DialogTypes) => void;
}

const RequestForQuotationUpload = ({
  matches,
  requestState,
  uploadedFilesError,
  handleUpdateRfqFile,
  handleBatchUpdateRfqFile,
  handleDeleteRfqFile,
  onOpenDialog
}: Props) => {
  const { control, errors } = useFormContext();

  const [toolTipOpen, setToolTipOpen] = useState<boolean>(true);

  const [selectedFile, setSelectFile] = useState<SelectFileDialogProps>(
    initSelectFile
  );
  const classes = useStyles();

  const uploadedDrawingFiles =
    requestState.uploadedDrawingFiles &&
    requestState.uploadedDrawingFiles.length > 0
      ? requestState.uploadedDrawingFiles
      : [];

  const isEmpty = uploadedDrawingFiles.length === 0;
  const isMultiple = uploadedDrawingFiles.length > 1;

  const selectedFileInfo =
    uploadedDrawingFiles.filter((file) => file.fileId === selectedFile.fileId)
      .length > 0
      ? uploadedDrawingFiles.filter(
          (file) => file.fileId === selectedFile.fileId
        )[0]
      : undefined;

  const onSelectFile = (payload: SelectFileDialogProps) => {
    setSelectFile({ ...payload });
  };

  useEffect(() => {
    const errorsToArray = Object.keys(errors);
    const filesErrors = errorsToArray.some(
      (key) =>
        key !== 'dueDate' &&
        key !== 'projectName' &&
        key !== 'projectPurpose' &&
        key !== 'maxEstimatedBudget'
    );

    if (errors['uploadedDrawingFiles'] || filesErrors) {
      document.getElementById('file-upload-box')?.scrollIntoView(true);
    }
  }, [errors]);

  return (
    <section
      id="file-upload-box"
      className={clsx('rfq-upload', classes['rfq-info-section'])}
    >
      {selectedFile.fileId && selectedFile.mode === 'change' && (
        <OptionSelectDialog
          onClose={() => setSelectFile(initSelectFile)}
          handleUpdateRfqFile={handleUpdateRfqFile}
          open={true}
          selectedFileInfo={selectedFileInfo}
          serviceType={requestState.serviceType}
          detailedServiceType={
            requestState.detailedServiceType as CapaQuotationDetailedServiceType
          }
          isMultiple={isMultiple}
          uploadedFilesError={uploadedFilesError}
          handleBatchUpdateRfqFile={handleBatchUpdateRfqFile}
          toolTipOpen={toolTipOpen}
          onCloseToolTip={() => setToolTipOpen(false)}
        />
      )}

      {selectedFile.mode === 'batch' && (
        <OptionSelectBatchDialog
          onClose={() => setSelectFile(initSelectFile)}
          uploadedFilesError={uploadedFilesError}
          handleBatchUpdateRfqFile={handleBatchUpdateRfqFile}
          open={true}
          selectedFileInfo={selectedFileInfo}
          serviceType={requestState.serviceType}
          detailedServiceType={
            requestState.detailedServiceType as CapaQuotationDetailedServiceType
          }
        />
      )}

      {selectedFile.fileId && selectedFile.mode === 'delete' && (
        <DeleteDrawingFileDialog
          onClose={() => setSelectFile(initSelectFile)}
          handleDeleteRfqFile={handleDeleteRfqFile}
          open={true}
          selectedFileInfo={selectedFileInfo}
        />
      )}

      {isEmpty ? (
        <Controller
          defaultValue={[]}
          name="uploadedDrawingFiles"
          control={control}
          rules={{
            validate: () => {
              return uploadedDrawingFiles.length !== 0;
            }
          }}
          render={() => (
            <div
              className={clsx('empty-box', {
                'error': errors['uploadedDrawingFiles']
              })}
            >
              <CustomizedTypography
                color={
                  errors['uploadedDrawingFiles'] ? 'error' : 'textSecondary'
                }
                variant="body2"
                align="center"
              >
                업로드한 도면이 없습니다. 도면 파일을 업로드 해주세요.
              </CustomizedTypography>
            </div>
          )}
        />
      ) : (
        <React.Fragment>
          {matches ? (
            <RequestForQuotationFileCard
              detailedServiceType={
                requestState.detailedServiceType as CapaQuotationDetailedServiceType
              }
              serviceType={requestState.serviceType}
              uploadedDrawingFiles={uploadedDrawingFiles}
              onSelectFile={onSelectFile}
            />
          ) : (
            <RequestForQuotationFileTable
              uploadedDrawingFiles={uploadedDrawingFiles}
              detailedServiceType={
                requestState.detailedServiceType as CapaQuotationDetailedServiceType
              }
              serviceType={requestState.serviceType}
              onSelectFile={onSelectFile}
            />
          )}
        </React.Fragment>
      )}

      <RequestForQuotationDropzone
        serviceType={requestState.serviceType}
        uploadedFilesCount={uploadedDrawingFiles.length}
        onOpenDialog={onOpenDialog}
      />

      {requestState.serviceType === CapaQuotationServiceType.Printing && (
        <PrintingTooltip />
      )}
    </section>
  );
};

export default RequestForQuotationUpload;

export interface SelectFileDialogProps {
  fileId?: string;
  mode: 'change' | 'delete' | 'batch' | 'default';
}

export const initSelectFile: SelectFileDialogProps = {
  fileId: undefined,
  mode: 'default'
};

export interface ServiceTypes {
  serviceType: CapaQuotationServiceType;
  detailedServiceType: CapaQuotationDetailedServiceType;
}

export const OptionComponent = ({
  serviceType,
  detailedServiceType,
  material,
  detailedMaterial,
  recommendation,
  etc,
  quantity
}: UploadedDrawingFileType & ServiceTypes) => {
  const isRecommend =
    detailedServiceType === CapaQuotationDetailedServiceType.Recommend;

  if (
    [CapaQuotationServiceType.Cnc, CapaQuotationServiceType.Inj].includes(
      serviceType as CapaQuotationServiceType
    )
  ) {
    return (
      <React.Fragment>
        {!isRecommend && material && (
          <CustomizedTypography variant="body2" color="textPrimary">
            - 재료 대분류 :&nbsp;
            {requestEnumToString(material)}
          </CustomizedTypography>
        )}

        {!isRecommend && material && detailedMaterial && (
          <CustomizedTypography variant="body2" color="textPrimary">
            - 재료 소분류 : {requestEnumToString(detailedMaterial)}
          </CustomizedTypography>
        )}

        {etc && (
          <CustomizedTypography variant="body2" color="textPrimary">
            - 기타 재료 : {etc}
          </CustomizedTypography>
        )}

        {(material === CapaQuotationMaterial.Recommend ||
          detailedMaterial === CapaQuotationDetailedMaterialType.Recommend ||
          detailedServiceType === CapaQuotationDetailedServiceType.Recommend) &&
        recommendation ? (
          <CustomizedTypography variant="body2" color="textPrimary">
            - 재료 특성 :&nbsp;
            {recommendation.map((attr) => requestEnumToString(attr)).join(', ')}
            <br />
            {quantity && `- 수량 : ${formatCurrency(quantity)}개`}
            <span className="errorMessage">
              파트너가 재료 추천에 참고할 수 있도록 가공 시 필요한 부분을 상세
              요청사항에 적어주세요.
            </span>
          </CustomizedTypography>
        ) : (
          quantity && (
            <CustomizedTypography variant="body2" color="textPrimary">
              - 수량 : {formatCurrency(quantity)}개
            </CustomizedTypography>
          )
        )}
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      {!isRecommend && material && (
        <CustomizedTypography variant="body2" color="textPrimary">
          - 재료 :&nbsp;
          {requestEnumToString(material as CapaQuotationMaterial)}
        </CustomizedTypography>
      )}

      {etc && (
        <CustomizedTypography variant="body2" color="textPrimary">
          - 기타 재료 : {etc}
        </CustomizedTypography>
      )}

      {(material === CapaQuotationMaterial.Recommend ||
        detailedServiceType === CapaQuotationDetailedServiceType.Recommend) &&
      recommendation ? (
        <CustomizedTypography variant="body2" color="textPrimary">
          - 재료 특성 :&nbsp;
          {recommendation.map((attr) => requestEnumToString(attr)).join(', ')}
          <br />
          {quantity && `- 수량 : ${formatCurrency(quantity)}개`}
          <span className="errorMessage">
            파트너가 재료 추천에 참고할 수 있도록 가공 시 필요한 부분을 상세
            요청사항에 적어주세요.
          </span>
        </CustomizedTypography>
      ) : (
        quantity && (
          <CustomizedTypography variant="body2" color="textPrimary">
            - 수량 : {formatCurrency(quantity)}개
          </CustomizedTypography>
        )
      )}
    </React.Fragment>
  );
};
